/**
 * Authoritative player state. Everything that influences the next tick lives
 * here (or in the immutable config / level), so a state plus an input
 * sequence reproduces the same trajectory under the same engine build.
 */
import { Vec3 } from '../core/Vec3';
import type { SurfaceMaterialDef } from '../world/SurfaceMaterials';

export type DeathCause = 'lethal-surface' | 'out-of-bounds' | 'numerical-fault';

/** Persistent per-rule state (e.g. stored impact energy). Must be resettable. */
export interface RuleState {
  reset(): void;
  /** Plain numeric snapshot for replay comparison and diagnostics. */
  snapshot(): readonly number[];
}

export interface ContactRecord {
  colliderId: number;
  materialId: string;
  kind: SurfaceMaterialDef['kind'];
  normal: Vec3;
  distance: number;
}

export class PlayerState {
  /** Bottom point of the capsule (feet). */
  readonly position = new Vec3();
  readonly velocity = new Vec3();
  /** Position at the start of the current tick; used for render interpolation. */
  readonly previousPosition = new Vec3();
  yaw = 0;
  pitch = 0;
  /** Unit vector opposite to gravity. Gravity manipulation will change this. */
  readonly up = new Vec3(0, 1, 0);
  gravityScale = 1;

  grounded = false;
  readonly groundNormal = new Vec3(0, 1, 0);
  groundColliderId = -1;
  groundMaterial: SurfaceMaterialDef | null = null;
  ticksSinceGrounded = 1_000_000;
  ticksSinceJump = 1_000_000;
  jumpBuffer = 0;

  // Phase 8 techniques (src/movement/Techniques.ts).
  crouched = false;
  /** crouchHeld on the previous tick (the press is derived from it). */
  crouchHeldPrev = false;
  sliding = false;
  /** Ticks until a slide may boost again. */
  slideCooldown = 0;
  /** Consecutive grounded ticks (0 in the air). */
  groundedTicks = 0;
  /** Ticks into the current wall-run (0: not wall-running). */
  wallRunTicks = 0;
  /**
   * The last wall within reach in the air: its outward normal, plane offset
   * (normal·point) and ticks since. Walls are identified by plane, not
   * collider, so a long wall made of several tiles is still one wall.
   */
  readonly wallNormal = new Vec3();
  wallOffset = 0;
  ticksSinceWall = 1_000_000;
  /** The speed the player was moving into that wall with when last touching it (hit or approach). */
  wallApproachSpeed = 0;
  /** The wall last wall-run or wall-jumped from; it cannot be used again until another wall or the ground. */
  usedWall = false;
  readonly usedWallNormal = new Vec3();
  usedWallOffset = 0;
  /** Wall-jumps since the ground. */
  wallJumps = 0;

  /** Surfaces within contact distance at the end of the last tick. */
  readonly contacts: ContactRecord[] = [];
  contactCount = 0;

  /** Per-rule movement modifiers, keyed by rule id. */
  readonly ruleState = new Map<string, RuleState>();

  tick = 0;
  alive = true;
  deathCause: DeathCause | null = null;
  /** True when the last move teleported (respawn), so rendering must not interpolate. */
  teleported = true;

  /** Places the player at a spawn point and clears all transient movement state. */
  respawn(position: Readonly<Vec3>, yaw: number): void {
    this.position.copy(position);
    this.previousPosition.copy(position);
    this.velocity.set(0, 0, 0);
    this.yaw = yaw;
    this.pitch = 0;
    this.up.set(0, 1, 0);
    this.gravityScale = 1;
    this.grounded = false;
    this.groundNormal.set(0, 1, 0);
    this.groundColliderId = -1;
    this.groundMaterial = null;
    this.ticksSinceGrounded = 1_000_000;
    this.ticksSinceJump = 1_000_000;
    this.jumpBuffer = 0;
    this.resetTechniques();
    this.contactCount = 0;
    for (const s of this.ruleState.values()) s.reset();
    this.alive = true;
    this.deathCause = null;
    this.teleported = true;
  }

  /**
   * Adopts the kinematic and rule state of `other` (used when a simulation is
   * rebuilt with a new configuration mid-session). Rule state objects are
   * shared, not cloned, so `other` must be discarded afterwards. Contact
   * records are recomputed on the next tick.
   */
  copyFrom(other: PlayerState): void {
    this.position.copy(other.position);
    this.previousPosition.copy(other.previousPosition);
    this.velocity.copy(other.velocity);
    this.yaw = other.yaw;
    this.pitch = other.pitch;
    this.up.copy(other.up);
    this.gravityScale = other.gravityScale;
    this.grounded = other.grounded;
    this.groundNormal.copy(other.groundNormal);
    this.groundColliderId = other.groundColliderId;
    this.groundMaterial = other.groundMaterial;
    this.ticksSinceGrounded = other.ticksSinceGrounded;
    this.ticksSinceJump = other.ticksSinceJump;
    this.jumpBuffer = other.jumpBuffer;
    this.crouched = other.crouched;
    this.crouchHeldPrev = other.crouchHeldPrev;
    this.sliding = other.sliding;
    this.slideCooldown = other.slideCooldown;
    this.groundedTicks = other.groundedTicks;
    this.wallRunTicks = other.wallRunTicks;
    this.wallNormal.copy(other.wallNormal);
    this.wallOffset = other.wallOffset;
    this.ticksSinceWall = other.ticksSinceWall;
    this.wallApproachSpeed = other.wallApproachSpeed;
    this.usedWall = other.usedWall;
    this.usedWallNormal.copy(other.usedWallNormal);
    this.usedWallOffset = other.usedWallOffset;
    this.wallJumps = other.wallJumps;
    this.contactCount = 0;
    this.ruleState.clear();
    for (const [k, s] of other.ruleState) this.ruleState.set(k, s);
    this.tick = other.tick;
    this.alive = other.alive;
    this.deathCause = other.deathCause;
    this.teleported = true;
  }

  resetTechniques(): void {
    this.crouched = false;
    this.crouchHeldPrev = false;
    this.sliding = false;
    this.slideCooldown = 0;
    this.groundedTicks = 0;
    this.wallRunTicks = 0;
    this.wallNormal.set(0, 0, 0);
    this.wallOffset = 0;
    this.ticksSinceWall = 1_000_000;
    this.wallApproachSpeed = 0;
    this.usedWall = false;
    this.usedWallNormal.set(0, 0, 0);
    this.usedWallOffset = 0;
    this.wallJumps = 0;
  }

  /** Numeric technique state, in a fixed order, for state hashes and replay comparison. */
  techniqueValues(): number[] {
    return [
      this.crouched ? 1 : 0,
      this.crouchHeldPrev ? 1 : 0,
      this.sliding ? 1 : 0,
      this.slideCooldown,
      Math.min(this.groundedTicks, 1e6),
      this.wallRunTicks,
      this.wallNormal.x,
      this.wallNormal.y,
      this.wallNormal.z,
      this.wallOffset,
      Math.min(this.ticksSinceWall, 1e6),
      this.wallApproachSpeed,
      this.usedWall ? 1 : 0,
      this.usedWallNormal.x,
      this.usedWallNormal.y,
      this.usedWallNormal.z,
      this.usedWallOffset,
      this.wallJumps,
    ];
  }

  speed(): number {
    return this.velocity.length();
  }

  /** Horizontal speed relative to the current up vector. */
  planarSpeed(): number {
    const vu = this.velocity.dot(this.up);
    return Math.sqrt(Math.max(0, this.velocity.lengthSq() - vu * vu));
  }

  snapshot(): PlayerSnapshot {
    const rules: Record<string, readonly number[]> = {};
    for (const [k, s] of this.ruleState) rules[k] = s.snapshot();
    return {
      tick: this.tick,
      position: [this.position.x, this.position.y, this.position.z],
      velocity: [this.velocity.x, this.velocity.y, this.velocity.z],
      yaw: this.yaw,
      grounded: this.grounded,
      alive: this.alive,
      rules,
    };
  }
}

export interface PlayerSnapshot {
  tick: number;
  position: [number, number, number];
  velocity: [number, number, number];
  yaw: number;
  grounded: boolean;
  alive: boolean;
  rules: Record<string, readonly number[]>;
}
